// @flow

import React, { type Element, PureComponent } from 'react';
import ImpressionTracker from 'app/common/lib/react/ImpressionTracker';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { SuggestionUser } from 'app/common/react/components/FollowingFeed/FollowingFeedTypes';
import UserFollowButton from 'app/common/react/components/FeedsAndFollowing/FollowButton/UserFollowButton';
import withViewer from 'app/common/lib/react/withViewer';
import { track } from 'app/common/lib/react/analytics';
import { Avatar, Box, Card, Icon, Image, Mask, Text } from 'gestalt';

const RECENT_PIN_IMAGE_SIZE = '222x';
const RECENT_PIN_IMAGE_COUNT = 3;

const AVATAR_SIZE = 100;

// z-index to raise avatar above recent pinned images to create layered effect
const styles = {
  avatar: {
    border: '2px solid white',
    zIndex: 1,
  },
};

type Props = {|
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  renderDescription: (user: SuggestionUser, slotIndex: number) => Element<*>,
  user: SuggestionUser,
  onFollow?: Function,
  viewer: AuthViewer,
  itemIdx: number,
  isNUX?: boolean,
|};

export class FollowingFeedManageUserGridRep extends PureComponent<Props> {
  getUserRecommendationReason = (isImpression?: boolean) => {
    const {
      user: { user_recommendation_reason: userRecommendationReason },
    } = this.props;
    if (!userRecommendationReason) {
      return {};
    }

    return isImpression
      ? {
          recommendationReasonDescription:
            userRecommendationReason.recommendation_reason_description,
          recommendationReasonDetails: userRecommendationReason.recommendation_reason_details,
          recommendationReasonType: userRecommendationReason.recommendation_reason_type,
        }
      : {
          recommendation_reason_description:
            userRecommendationReason.recommendation_reason_description,
          recommendation_reason_details: userRecommendationReason.recommendation_reason_details,
          recommendation_reason_type: userRecommendationReason.recommendation_reason_type,
        };
  };

  onUserFollow = (id: string, state: boolean) => {
    const { onFollow, isNUX } = this.props;

    if (state) {
      track(PCONST.thrift.EventType.USER_FOLLOW, {
        objectId: id,
        view: PCONST.thrift.ViewType.USER_FOLLOW_FEED,
        viewParameter: PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS,
        element: PCONST.thrift.ElementType.USER_FOLLOW,
        ...this.getUserRecommendationReason(),
      });

      if (isNUX) {
        track(PCONST.thrift.EventType.USER_FOLLOW, {
          objectId: id,
          view: PCONST.thrift.ViewType.FOLLOWING_TAB_NUX,
          viewParameter: PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS,
          element: PCONST.thrift.ElementType.USER_FOLLOW,
          ...this.getUserRecommendationReason(),
        });
      }
    } else {
      track(PCONST.thrift.EventType.USER_UNFOLLOW, {
        objectId: id,
        view: PCONST.thrift.ViewType.USER_FOLLOW_FEED,
        viewParameter: PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS,
        element: PCONST.thrift.ElementType.USER_FOLLOW,
        ...this.getUserRecommendationReason(),
      });
    }

    if (onFollow) {
      onFollow(id, state);
    }
  };

  onSuggestionAvatarClick = (id: string) => {
    const { isNUX } = this.props;

    track(PCONST.thrift.EventType.CLICK, {
      objectId: id,
      view: PCONST.thrift.ViewType.USER_FOLLOW_FEED,
      viewParameter: PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS,
      element: PCONST.thrift.ElementType.PINNER_MODULE,
      ...this.getUserRecommendationReason(),
    });

    if (isNUX) {
      track(PCONST.thrift.EventType.CLICK, {
        objectId: id,
        view: PCONST.thrift.ViewType.FOLLOWING_TAB_NUX,
        viewParameter: PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS,
        element: PCONST.thrift.ElementType.PINNER_MODULE,
        ...this.getUserRecommendationReason(),
      });
    }
  };

  onSuggestionPinRepClick = (id: string) => {
    track(PCONST.thrift.EventType.CLICK, {
      objectId: id,
      view: PCONST.thrift.ViewType.USER_FOLLOW_FEED,
      viewParameter: PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS,
      element: PCONST.thrift.ElementType.USER_PINS_REP,
      ...this.getUserRecommendationReason(),
    });
  };

  wrapClickthrough = (child: Element<any>, onClick: (id: string) => void) => (
    <NavigateLink
      to={`/${this.props.user.username}/`}
      onClick={() => onClick(this.props.user.id)}
      internalNewTab
    >
      {child}
    </NavigateLink>
  );

  render() {
    const { renderDescription, user, itemIdx, isNUX } = this.props;
    const {
      id,
      username,
      verified_identity: verifiedIdentity,
      recent_pin_images: pinImages,
      full_name: fullName,
      image_large_url: imageLargeUrl,
    } = user;
    const showVerifiedIcon = verifiedIdentity && verifiedIdentity.verified;

    const images = pinImages[RECENT_PIN_IMAGE_SIZE];
    const recentPinImages =
      images &&
      images.slice(0, RECENT_PIN_IMAGE_COUNT).map((image, index) => (
        <Box display="flex" key={index}>
          <Mask wash width={AVATAR_SIZE} height={AVATAR_SIZE}>
            <Image
              fit="cover"
              src={image.url}
              alt={user.username}
              naturalHeight={image.height}
              naturalWidth={image.width}
            />
          </Mask>
          {index < 2 && <Box width={2} />}
        </Box>
      ));

    // Appending empty gray boxes if user has less than displayed pin image count
    if (recentPinImages && recentPinImages.length < RECENT_PIN_IMAGE_COUNT) {
      const numEmpty = RECENT_PIN_IMAGE_COUNT - recentPinImages.length;
      for (let i = 0; i < numEmpty; i += 1) {
        recentPinImages.push(
          <Box display="flex" key={i + numEmpty}>
            <Box color="lightGray" width={AVATAR_SIZE} height={AVATAR_SIZE} />
            {i < numEmpty - 1 && <Box width={2} />}
          </Box>,
        );
      }
    }

    const descriptionRep = renderDescription(user, itemIdx);

    const profileRep = (
      <Box display="flex" height={AVATAR_SIZE}>
        <Box
          position="absolute"
          width={AVATAR_SIZE}
          height={AVATAR_SIZE}
          dangerouslySetInlineStyle={{ __style: styles.avatar }}
          shape="circle"
        >
          <Avatar name={fullName} src={imageLargeUrl} />
        </Box>
        <Box flex="grow" display="flex" shape="rounded" overflow="hidden" justifyContent="end">
          {recentPinImages}
        </Box>
      </Box>
    );

    const footerRep = (
      <Box display="flex" maxWidth={270}>
        <Text size="lg" truncate weight="bold">
          {fullName}
        </Text>
        {showVerifiedIcon && (
          <Box display="flex" paddingX={2} alignItems="center" flex="grow" minWidth={30}>
            <Icon color="red" icon="check-circle" accessibilityLabel="" size={14} />
          </Box>
        )}
      </Box>
    );

    const wrappedDescription = this.wrapClickthrough(descriptionRep, this.onSuggestionAvatarClick);
    const wrappedProfile = this.wrapClickthrough(profileRep, this.onSuggestionPinRepClick);
    const wrappedFooter = this.wrapClickthrough(footerRep, this.onSuggestionAvatarClick);

    const item = (
      <Box display="flex" justifyContent="center" direction="column" key={id} padding={1}>
        <Card>
          {wrappedProfile}
          <Box display="flex" marginTop={3} marginBottom={1}>
            <Box marginRight={2} flex="grow" direction="column">
              {wrappedFooter}
              <Box height={2} />
              {wrappedDescription}
            </Box>
            <Box flex="none">
              <UserFollowButton
                fullName={fullName}
                hideFollowingText
                id={id}
                imageUrl={imageLargeUrl || ''}
                inline
                isOwnProfile={username === this.props.viewer.username}
                profileUrl={imageLargeUrl}
                onUserFollow={(state) => this.onUserFollow(id, state)}
                suppressToasts
              />
            </Box>
          </Box>
        </Card>
      </Box>
    );

    const trackedItem = isNUX ? (
      <ImpressionTracker
        loggingId={id}
        impressionType={'User'}
        impressionAuxFields={this.getUserRecommendationReason(true)}
        slotIndex={itemIdx}
        viewType={PCONST.thrift.ViewType.FOLLOWING_TAB_NUX}
        viewParameter={PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS}
      >
        {item}
      </ImpressionTracker>
    ) : (
      item
    );

    return (
      <ImpressionTracker
        componentType={PCONST.thrift.ComponentType.SUGGESTED_USERS}
        loggingId={id}
        impressionType={'User'}
        impressionAuxFields={this.getUserRecommendationReason(true)}
        slotIndex={itemIdx}
        viewType={PCONST.thrift.ViewType.USER_FOLLOW_FEED}
        viewParameter={PCONST.thrift.ViewParameterType.CREATOR_RECOMMENDATIONS}
      >
        {trackedItem}
      </ImpressionTracker>
    );
  }
}

export default withViewer(FollowingFeedManageUserGridRep);
